// noinspection DuplicatedCode

import React, { useEffect, useState } from 'react';
import './Partner.scss';
import {
  Button,
  ButtonGroup,
  Col,
  Form,
  FormGroup,
  Row,
} from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { useLocation, useNavigate, useParams } from 'react-router';
import Contents from 'components/Layout/Contents';
import {
  GroupFormCheck,
  Input,
  InputSocialMedia,
  MarkDownEditor,
  Select,
} from 'components/Forms';
import PartnerService from 'api/PartnerService';
import UploadImage from 'components/UploadImage/UploadImage';

import TierService from 'api/TierService';
import Spinner from 'components/Spinner/Spinner';
import CompetencyService from 'api/CompetencyService';
import Alert from 'components/Alert/Alert';
import useLayout from 'components/Layout/useLayout';

export default function EditPartner() {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [disabledFields, setDisabledFields] = useState(true);

  const [firstLoad, setFirstLoad] = useState(true);

  const { scrollToTop } = useLayout();

  const fetchPartner = useQuery(
    ['partners', partnerId],
    () => {
      setDisabledFields(true);
      return PartnerService.getOneById(partnerId);
    },
    {
      enabled: false,
      onSuccess: (data) => {
        if (data.data) {
          const response = data.data;

          setValue('id', response.id);
          setValue('name', response.name);
          setValue('title', response.title);
          setValue('tierId', response.tierId);
          setValue('templateId', response.templateId);
          setValue('description', response.description);
          setValue('email', response.email ? response.email : '');
          setValue('phone', response.phone ? response.phone : '');
          setValue('website', response.website);
          setValue(
            'status',
            response.status === 'active' ? 'Active' : response.status
          );
          setValue(
            'address[street]',
            response?.address?.street ? response?.address?.street : ''
          );
          setValue(
            'address[suite]',
            response?.address?.suite ? response?.address?.suite : ''
          );
          setValue(
            'address[city]',
            response?.address?.city ? response?.address?.city : ''
          );
          setValue(
            'address[zipCode]',
            response?.address?.zipCode ? response?.address?.zipCode : ''
          );
          setValue(
            'address[state]',
            response?.address?.state ? response?.address?.state : ''
          );
          setValue(
            'address[country]',
            response?.address?.country ? response?.address?.country : ''
          );
          setValue('logo', response?.logo ? response?.logo : '');
          setValue('specialties', response.specialties);
          setIconSrc(response.logo ? response.logo.toString() : '');

          const socialMedia = {};
          response.socialMedia.forEach((sm) => {
            socialMedia[sm.network] = {
              network: sm.isVisible ? sm.network : false,
              location:
                sm.location !== null && sm.location.toString() !== 'null'
                  ? sm.location.toString()
                  : '',
            };
          });
          setValue('socialMedia', socialMedia);
        }
        setDisabledFields(false);
        setFirstLoad(false);
      },
    }
  );

  const mutation = useMutation(
    (newPartner) => PartnerService.update(partnerId, newPartner),
    {
      onSuccess: () => {
        scrollToTop();
        fetchPartner.refetch();
      },
    }
  );

  const [specialties, setSpecialties] = useState([]);

  const mutationGetSpecialties = useMutation(() => CompetencyService.getAll(), {
    onSuccess: (response) => {
      if (response && response.data) {
        const temp = [];
        response.data.map((sp) => {
          temp.push({
            value: sp.id,
            label: sp.name,
          });
          return sp;
        });
        setSpecialties(temp);
      }
    },
  });
  const tierQuery = useQuery('list-tiers', TierService.listAll);

  const [tierId, setTierId] = useState('');
  const [tierTemplates, setTierTemplates] = useState([]);
  const [iconSrc, setIconSrc] = useState('');
  const statusOptions = [
    {
      value: 'Active',
      label: 'Active',
    },
    {
      value: 'Registered',
      label: 'Registered',
    },
    {
      value: 'Draft',
      label: 'Draft',
    },
  ];
  const { handleSubmit, control, register, watch, setValue, getValues } =
    useForm({
      defaultValues: {
        name: '',
        title: '',
        tierId: '',
        templateId: '',
        description: '',
        email: '',
        phone: '',
        website: '',
        status: '',
        address: {
          street: '',
          suite: '',
          city: '',
          zipCode: '',
          state: '',
          country: '',
        },
        socialMedia: [],
        specialties: [],
        logo: '',
      },
    });

  const templateByTierQuery = useQuery(
    ['template-options', tierId],
    () => TierService.getTemplatesById(tierId),
    {
      enabled: false,
      onSuccess: (data) => {
        setTierTemplates(data);
        if (!data.find((t) => t.value === getValues().templateId)) {
          setValue('templateId', '');
        }
      },
    }
  );

  const onSubmit = (formData) => {
    const payload = { ...formData };
    scrollToTop();
    if (formData.socialMedia) {
      let socialMedia = [];
      Object.keys(formData.socialMedia).forEach((element) => {
        const newValue = {
          ...formData.socialMedia[element],
          isVisible: !!formData.socialMedia[element].network,
          network: element,
        };
        socialMedia = [...socialMedia, newValue];
      });

      payload.socialMedia = socialMedia;
    }
    payload.logo = iconSrc;
    setTimeout(() => mutation.mutate(payload), 500);
  };

  const isDataProcessing = () => mutation.isLoading || disabledFields;

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'tierId') {
        setTierId(value[name]);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (tierId && tierId.toString().length > 0) {
      templateByTierQuery.refetch();
    }
  }, [tierId]);

  useEffect(() => {
    if (partnerId) {
      fetchPartner.refetch();
    }
  }, [partnerId]);

  useEffect(() => {
    mutationGetSpecialties.mutate();
    scrollToTop();
  }, []);

  if ((fetchPartner.isLoading || disabledFields) && firstLoad) {
    return <Spinner />;
  }

  return (
    <Contents>
      {tierQuery.isLoading && firstLoad && fetchPartner.isLoading && (
        <Spinner />
      )}
      {tierQuery.isSuccess && firstLoad && tierQuery.data.data.length === 0 ? (
        <Alert
          variant="warning"
          iconProps={{ className: 'me-1' }}
          data-testid="Alert-noTiers"
        >
          Please add a tier first before updating a partner.{' '}
          <Link to="/tiers/add">Click here</Link> to add a tier.
        </Alert>
      ) : (
        <>
          <div className="d-flex mb-3">
            <h2 className="page-content-header mb-0">Edit Partners</h2>
            <div className="flex-grow-1 d-flex align-content-center align-items-center justify-content-end  text-end">
              <ButtonGroup size="sm">
                <Button
                  variant="outline-primary"
                  onClick={() => {
                    navigate(`/partners`, {
                      state: { search: location.state?.search || '' },
                    });
                  }}
                >
                  Back to List
                </Button>
              </ButtonGroup>
            </div>
          </div>
          <Form
            onSubmit={handleSubmit(onSubmit)}
            id="frmAddPartner"
            encType="multipart/form-data"
          >
            {mutation.isLoading && (
              <Alert
                variant="info"
                title="Processing"
                iconProps={{ spin: true, pulse: true }}
              >
                Updating partner...
              </Alert>
            )}
            {
              // eslint-disable-next-line react/prop-types
              mutation.isError && (
                <Alert variant="danger" title="Error">
                  An error occurred: {mutation.error.message}
                </Alert>
              )
            }
            {
              // eslint-disable-next-line react/prop-types
              mutation.isSuccess && (
                <Alert variant="success" title="Processing">
                  Partner details updated successfully.
                </Alert>
              )
            }
            <Row>
              <Col sm={8}>
                <FormGroup>
                  <Input
                    name="name"
                    label="Name"
                    control={control}
                    rules={{ required: true }}
                    disabled={isDataProcessing()}
                    placeholder="Name"
                  />
                </FormGroup>
                <FormGroup>
                  <Input
                    name="title"
                    label="Title"
                    control={control}
                    rules={{ required: true }}
                    disabled={isDataProcessing()}
                    placeholder="Title"
                  />
                </FormGroup>
                <FormGroup>
                  <MarkDownEditor
                    name="description"
                    label="Description"
                    control={control}
                    limit={1000}
                    disabled={isDataProcessing()}
                  />
                </FormGroup>
                <FormGroup>
                  <Input
                    name="email"
                    label="Email"
                    type="email"
                    placeholder="Email address"
                    control={control}
                    disabled={isDataProcessing()}
                  />
                </FormGroup>
                <FormGroup>
                  <Input
                    name="phone"
                    label="Phone"
                    type="phone"
                    control={control}
                    placeholder="Phone"
                    disabled={isDataProcessing()}
                  />
                </FormGroup>
                <FormGroup>
                  <Input
                    type="url"
                    name="website"
                    label="Website"
                    control={control}
                    placeholder="Full website url"
                    rules={{ required: true }}
                    disabled={isDataProcessing()}
                  />
                </FormGroup>
                <FormGroup>
                  <div className="form input address">
                    <Row>
                      <Col sm={3}>
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label
                          htmlFor="address-street"
                          className="col-form-label"
                        >
                          Address
                        </label>
                      </Col>
                      <Col sm={9}>
                        <div className="d-block mb-3">
                          <Input
                            name="address[street]"
                            control={control}
                            placeholder="Street"
                            disabled={isDataProcessing()}
                          />
                        </div>
                        <div className="d-block mb-3">
                          <Input
                            name="address[suite]"
                            control={control}
                            placeholder="Suite"
                            disabled={isDataProcessing()}
                          />
                        </div>
                        <div className="d-block mb-3">
                          <Row>
                            <Col sm={6}>
                              <Input
                                name="address[city]"
                                control={control}
                                placeholder="City"
                                disabled={isDataProcessing()}
                              />
                            </Col>
                            <Col sm={6}>
                              <Input
                                name="address[zipCode]"
                                control={control}
                                placeholder="Zip / Postal Code"
                                disabled={isDataProcessing()}
                              />
                            </Col>
                          </Row>
                        </div>
                        <div className="d-block mb-3">
                          <Input
                            name="address[state]"
                            control={control}
                            placeholder="State / Region"
                            disabled={isDataProcessing()}
                          />
                        </div>
                        <div className="d-block mb-3">
                          <Input
                            name="address[country]"
                            control={control}
                            placeholder="Country"
                            disabled={isDataProcessing()}
                            errorMessage="Country is required"
                          />
                        </div>
                      </Col>
                    </Row>
                  </div>
                </FormGroup>
                <FormGroup>
                  <InputSocialMedia
                    label="Social"
                    register={register}
                    disabled={isDataProcessing()}
                  />
                </FormGroup>
                <FormGroup>
                  <div className="d-block specialty-list">
                    {specialties && specialties.length > 0 && (
                      <GroupFormCheck
                        label="Specialties"
                        name="specialties"
                        control={control}
                        items={specialties}
                        register={register}
                        disabled={
                          mutationGetSpecialties.isLoading || isDataProcessing()
                        }
                      />
                    )}
                  </div>
                </FormGroup>
              </Col>
              <Col sm={4}>
                <div className="d-flex justify-content-end">
                  <UploadImage
                    name="file"
                    title="Logo"
                    srcName="logo"
                    src={iconSrc}
                    setIconSrc={setIconSrc}
                    register={register}
                  />
                </div>

                <div className="partner select-container">
                  <Select
                    label="Tier"
                    name="tierId"
                    options={tierQuery.data?.data}
                    control={control}
                    disabled={tierQuery.isLoading || isDataProcessing()}
                    rules={{ required: true }}
                    block
                  />
                </div>

                <Select
                  label="Template"
                  name="templateId"
                  options={tierTemplates}
                  control={control}
                  disabled={
                    templateByTierQuery.isLoading ||
                    tierQuery.isLoading ||
                    isDataProcessing()
                  }
                  rules={{ required: true }}
                  block
                />

                <Select
                  label="Status"
                  name="status"
                  options={statusOptions}
                  control={control}
                  rules={{ required: true }}
                  disabled={isDataProcessing()}
                  block
                />
              </Col>
            </Row>
            <FormGroup>
              <Button
                variant="primary"
                className="btn-pmi"
                type="submit"
                disabled={isDataProcessing()}
              >
                Save
              </Button>
              <Link
                as={Button}
                to={`/partners/${partnerId}/profile`}
                className="d-inline-block ms-3"
              >
                Cancel
              </Link>
            </FormGroup>
          </Form>
        </>
      )}
    </Contents>
  );
}
